import { ChangeDetectionStrategy, Component, OnInit, ViewContainerRef, computed, inject, input, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideChevronRight, lucideGift } from '@ng-icons/lucide';
import {
  AccountMenuComponent,
  ThemeSegmentComponent,
  type AccountMenuItem,
  type ThemeSegmentMode,
} from '@lite-workspace/shared/blocks/account-menu';
import { OnboardingService, supportChannels } from '@lite-workspace/shared/onboarding-service';
import { HlmDialogOpener } from '@lite-workspace/shared/helm-ui/dialog';
import { AccountFacade } from '../domain/application/account.facade';
import { BalancePopupsStore } from '../domain/+state/balance-popups.store';
import { ThemeService } from '@lite-workspace/shared/theme';
import { BILLING_ENABLED } from '../../../core/app-config';
import { PartnerStudioResource } from '../../partner/domain/infrastructure/partner-cabinet.resource';
import { UiAnalyticsService } from '../../../core/ui-analytics.service';
import { BalanceDialogComponent } from './balance-dialog.component';
import { BonusDialogComponent } from './bonus-dialog.component';

/**
 * Fotoclass's side of the shared profile menu: the markup comes from the shared lib and only the
 * app-specific parts live here — billing rows, the balance pill and bonus banner in the header slot,
 * the theme toggle in the footer slot, and the sign-out.
 * The wrapper carries its own tag: `app-account-menu` inside this template is the SHARED component,
 * and reusing that selector here would make both match the same node (NG8023).
 */
@Component({
  selector: 'app-fotoclass-account-slot',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AccountMenuComponent, ThemeSegmentComponent, NgIcon],
  viewProviders: [provideIcons({ lucideChevronRight, lucideGift })],
  template: `
    <app-account-menu
      [items]="items()"
      [align]="align()"
      [nameOverride]="studio()?.name ?? null"
      (opened)="onMenuOpened()"
      (logoutConfirmed)="onLogoutConfirmed()"
    >
      <!-- Every header extra carries explicit grid placement: they land in the shared header's
           3-column grid, where auto-placement would collide with the email line. -->
      @if (isPartner()) {
        <p accountMenuHeader class="col-span-2 col-start-2 row-start-3 mt-0.5 text-xs text-c3">Партнёр</p>
      }

      @if (showBilling()) {
        <div accountMenuHeader class="col-start-3 row-start-1 self-start">
          <button type="button" [class]="balancePillClass" (click)="openBalance('balance_pill')">{{ balanceRub() }} ₽</button>
        </div>
      }

      @if (showBilling()) {
        <div accountMenuHeader class="col-span-3 col-start-1 row-start-3">
          <button type="button" [class]="bonusBannerClass" (click)="openBonus()">
            <ng-icon name="lucideGift" class="shrink-0 text-xl text-acc2" />
            <div class="flex min-w-0 flex-1 flex-col gap-0.75">
              <p class="text-sm font-semibold text-acc2">
                {{ facade.bonusReceived() ? 'Бонусы на старт' : 'Получить бонус 10 000 ₽' }}
              </p>
              <p class="text-xs text-c3">для первых 1000 фотографов</p>
            </div>
            <ng-icon name="lucideChevronRight" class="shrink-0 text-lg text-acc2" />
          </button>
        </div>
      }

      <app-theme-segment accountMenuFooter [mode]="themeMode()" (modeSelected)="setTheme($event)" />
    </app-account-menu>
  `,
})
export class AccountMenuHostComponent implements OnInit {
  readonly billingEnabled = input(BILLING_ENABLED);
  /** Menu edge to align with the trigger: 'end' fits a screen-right avatar (cabinet topbar);
   *  'start' opens rightwards for a screen-left avatar (wizard shell app-bar). */
  readonly align = input<'start' | 'end'>('end');

  private readonly menu = viewChild(AccountMenuComponent);

  protected readonly facade = inject(AccountFacade);
  private readonly router = inject(Router);
  private readonly dialogs = inject(HlmDialogOpener);
  private readonly vcr = inject(ViewContainerRef);
  private readonly channels = supportChannels();
  private readonly theme = inject(ThemeService);
  private readonly uiAnalytics = inject(UiAnalyticsService);
  private readonly balancePopups = inject(BalancePopupsStore);
  private readonly partnerStudios = inject(PartnerStudioResource);
  // Та же дверь в уроки, что и знак вопроса в шапке: и аудиторию, и открытие библиотеки считает сам
  // онбординг — своей проверки у меню нет.
  private readonly lessons = inject(OnboardingService);

  protected readonly themeMode = this.theme.resolvedTheme;

  // Partner-hood belongs to the ACCOUNT — the same studio binding the shell and the `/partner` guard
  // read. A partner sells templates instead of buying albums, so the whole billing side of the menu
  // (balance pill, bonus banner, «Стоимость») is not theirs, and the header leads with the studio.
  // Three states, not two: `undefined` is «not answered yet», and until then the menu shows neither the
  // billing side nor the studio header — a guess either way would flash and be taken back a beat later.
  protected readonly studio = this.partnerStudios.studio;
  protected readonly isPartner = computed(() => !!this.studio());
  protected readonly showBilling = computed(() => this.studio() !== undefined && this.billingEnabled() && !this.isPartner());

  protected readonly balanceRub = computed(() => new Intl.NumberFormat('ru-RU').format(this.facade.balanceAmount()));

  protected readonly items = computed<readonly AccountMenuItem[]>(() => [
    ...(this.lessons.audience() && this.lessons.hasLessons()
      ? [
          {
            id: 'lessons',
            label: 'Видеоуроки',
            icon: 'play' as const,
            accent: true,
            action: () => this.lessons.openLibrary(),
          },
        ]
      : []),
    ...(this.showBilling()
      ? [{ id: 'pricing', label: 'Стоимость', icon: 'wallet' as const, action: () => this.openBalance('pricing_item') }]
      : []),
    { id: 'settings', label: 'Настройки', icon: 'gear' as const, action: () => this.goSettings() },
    {
      id: 'support',
      label: 'Поддержка',
      icon: 'help-circle' as const,
      action: () => this.openSupportMenu(),
      children: this.channels().map(channel => ({
        id: channel.kind,
        label: channel.label,
        icon: channel.icon,
        separatorAfter: channel.separatorAfter,
        action: () => this.openSupportChannel(channel.kind, channel.open),
      })),
    },
    // Подсказки переехали сюда из меню «?»: последняя строка списка — значит, прямо над «Выйти», за
    // разделителем меню. На экране без подсказок строки нет: нажимать её было бы не на что.
    ...(this.lessons.screenHintCount() > 0
      ? [
          {
            id: 'hints',
            label: 'Подсказки на экране',
            icon: 'info' as const,
            badge: this.lessons.screenHintCount(),
            action: () => this.lessons.replayScreen(),
          },
        ]
      : []),
  ]);

  protected readonly balancePillClass =
    'shrink-0 cursor-pointer outline-none border border-acc-line bg-acc-softer text-acc2 ' +
    'rounded-full px-3 py-1.5 text-xs font-semibold transition-colors hover:bg-acc-soft focus-visible:bg-acc-soft';

  protected readonly bonusBannerClass =
    'mt-3.5 flex items-center text-left cursor-pointer outline-none gap-3 w-full border border-acc-line bg-acc-softer ' +
    'rounded-[var(--r-l)] py-3.25 px-3.5 transition-colors hover:bg-acc-soft focus-visible:bg-acc-soft';

  ngOnInit(): void {
    this.facade.ensureProfile();
    if (this.billingEnabled()) {
      this.facade.loadBilling();
    }

    // The menu asks for the studio itself — it used to, through the `toSignal(from(myStudio()))` line the
    // shared signal replaced, and nothing took the question over. Two surfaces mount this menu outside any
    // guard that asks: `/onboarding` (`app.routes.ts`, no `canActivate`) and `/designs/tunnel` (whose
    // `clientCabinetGuard` never touches the studio). There the signal stays `undefined` for the whole
    // visit, and `showBilling` — three-state on purpose — hides the balance pill, the bonus banner and
    // «Стоимость» from the very people the onboarding banner is written for. The promise is memoised, so
    // every other mount just reads the answer already on its way.
    void this.partnerStudios.myStudio();
  }

  protected openBalance(source: 'balance_pill' | 'pricing_item'): void {
    if (source === 'balance_pill') this.uiAnalytics.balanceViewed();
    else this.uiAnalytics.pricingViewed();
    this.closeMenu();
    // Load-once legs are no-ops; a leg that failed (tariff down) is asked again — the dialog reads the facade
    // live, so the answer lands in the window already open.
    this.facade.loadBilling();
    this.dialogs.open(BalanceDialogComponent, this.vcr, {});
  }

  // The pill and the banner are projected from THIS view, so they cannot be cdkMenuItems and never close
  // the panel themselves — without this the dropdown stays parked under the dialog scrim.
  private closeMenu(): void {
    this.menu()?.close();
  }

  protected openBonus(): void {
    this.uiAnalytics.bonusPromoClicked();
    this.closeMenu();
    // A manual open spends the promo's once-per-session budget too, or the countdown would drop a second
    // identical window on top of the one the photographer just opened from the banner.
    this.balancePopups.markBonusShown();
    this.dialogs.open(BonusDialogComponent, this.vcr, {});
  }

  protected setTheme(mode: ThemeSegmentMode): void {
    if (this.theme.resolvedTheme() !== mode) this.uiAnalytics.themeSwitched(mode);
    this.theme.setPreference(mode);
  }

  protected goSettings(): void {
    this.uiAnalytics.settingsOpened();
    void this.router.navigate(['/account']);
  }

  // Подменю раскрылось — ссылку в Telegram минтим сейчас: к нажатию строки она готова.
  private openSupportMenu(): void {
    this.uiAnalytics.supportOpened();
    for (const channel of this.channels()) channel.prepare?.();
  }

  protected openSupportChannel(kind: string, open: () => void): void {
    this.uiAnalytics.supportChannelPicked(kind);
    open();
  }

  protected onMenuOpened(): void {
    this.uiAnalytics.profileMenuOpened();
  }

  protected onLogoutConfirmed(): void {
    this.uiAnalytics.logout('account_menu');
    void this.facade.logout();
  }
}
